// The browser's only door to the Worker. Every call is same-origin with the session cookie; every change
// carries the X-Radar header, which a cross-site form can't send.

import type { Deal, DealResponse, Network, PrivateNote, PrivateState, SourceKind } from "../../shared/network";
import { gunzipJson, gzipJson } from "./gzip";

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

export interface Session {
  workspace: string;
  label: string;
  network: { generated: string; contacts: number; bytes: number; builtAt: string } | null;
  claude: boolean; // the Worker has an Anthropic API key
  monday: { connected: boolean; account?: string };
}

/** One uploaded (or Monday-pulled) source, already mapped to canonical rows (see client/sources). */
export interface SourceMeta {
  id: string;
  kind: SourceKind;
  label: string; // "Monday board: Investor Network"
  filename: string;
  rows: number;
  uploadedAt: string;
  origin: "file" | "monday";
  event?: string; // conference lists: event name
  date?: string; // conference lists: event date (ISO)
}
export interface StoredSource {
  meta: SourceMeta;
  rows: unknown[];
}

export interface TokenInfo {
  id: string;
  label: string;
  createdAt: string;
  lastUsedAt: string | null;
}
export interface MondayBoard {
  id: string;
  name: string;
  items: number | null;
}
export interface ActivityRow {
  ts: string;
  action: string;
  detail: string;
}
export interface AiPayload {
  system: string;
  prompt: string;
  schema: object; // JSON schema for the result
  effort?: "low" | "medium" | "high";
}

const enc = encodeURIComponent;

async function call(method: string, path: string, body?: unknown, extra: RequestInit = {}): Promise<Response> {
  const headers: Record<string, string> = { ...(extra.headers as Record<string, string>) };
  if (method !== "GET") headers["X-Radar"] = "1";
  let payload: BodyInit | undefined;
  if (body instanceof Uint8Array) {
    payload = body as BodyInit;
    headers["Content-Type"] ??= "application/octet-stream";
  } else if (body !== undefined) {
    payload = JSON.stringify(body);
    headers["Content-Type"] = "application/json";
  }
  const res = await fetch(path, { method, headers, body: payload, credentials: "same-origin" });
  if (!res.ok) {
    let msg = res.statusText;
    try {
      msg = ((await res.json()) as { error?: string }).error || msg;
    } catch {
      /* not JSON */
    }
    throw new ApiError(res.status, msg);
  }
  return res;
}
const json = async <T>(method: string, path: string, body?: unknown): Promise<T> => {
  const res = await call(method, path, body);
  return (res.status === 204 ? undefined : await res.json()) as T;
};

function b64urlJson(v: unknown): string {
  const bytes = new TextEncoder().encode(JSON.stringify(v));
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export const api = {
  async session(): Promise<Session | null> {
    try {
      return await json<Session>("GET", "/api/session");
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) return null;
      throw e;
    }
  },
  signIn: (code: string) => json<Session>("POST", "/api/signin", { code }),
  signOut: () => json<void>("POST", "/api/signout", {}),

  /** The merged network, or null before the first build. */
  async getNetwork(variant: "full" | "slim" = "full"): Promise<Network | null> {
    try {
      const res = await call("GET", `/api/network?variant=${variant}`);
      return await gunzipJson<Network>(await res.arrayBuffer());
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) return null;
      throw e;
    }
  },
  async putNetwork(net: Network, slim?: unknown): Promise<void> {
    await call("PUT", "/api/network?variant=full", await gzipJson(net), {
      headers: { "X-Radar-Meta": b64urlJson({ generated: net.generated, contacts: net.contacts.length }) },
    });
    if (slim !== undefined) await call("PUT", "/api/network?variant=slim", await gzipJson(slim));
  },

  listSources: async () => (await json<{ sources: SourceMeta[] }>("GET", "/api/sources")).sources,
  async getSource(id: string): Promise<StoredSource> {
    const res = await call("GET", `/api/sources/${enc(id)}`);
    return gunzipJson<StoredSource>(await res.arrayBuffer());
  },
  async putSource(src: StoredSource): Promise<void> {
    await call("PUT", `/api/sources/${enc(src.meta.id)}`, await gzipJson(src), { headers: { "X-Radar-Meta": b64urlJson(src.meta) } });
  },
  deleteSource: (id: string) => json<void>("DELETE", `/api/sources/${enc(id)}`),

  getState: () => json<PrivateState>("GET", "/api/state"),
  putNote: (k: string, note: PrivateNote | null) =>
    note ? json<void>("PUT", `/api/notes/${enc(k)}`, note) : json<void>("DELETE", `/api/notes/${enc(k)}`),
  putResponse: (dealId: string, k: string, r: DealResponse | null) =>
    r ? json<void>("PUT", `/api/responses/${enc(dealId)}/${enc(k)}`, { response: r }) : json<void>("DELETE", `/api/responses/${enc(dealId)}/${enc(k)}`),
  putContacted: (k: string, date: string | null) =>
    date ? json<void>("PUT", `/api/contacted/${enc(k)}`, { date }) : json<void>("DELETE", `/api/contacted/${enc(k)}`),
  putDeal: (deal: Deal) => json<void>("PUT", `/api/deals/${enc(deal.deal_id)}`, deal),
  deleteDeal: (id: string) => json<void>("DELETE", `/api/deals/${enc(id)}`),

  /** Generic JSON storage for the Lists page: paths under /api/lists/ only. Resolves null when nothing is saved. */
  async get<T = unknown>(path: string): Promise<T | null> {
    if (!path.startsWith("/api/lists/")) throw new Error("api.get only reads /api/lists/*");
    try {
      return await json<T>("GET", path);
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) return null;
      throw e;
    }
  },
  async put(path: string, body: unknown): Promise<void> {
    if (!path.startsWith("/api/lists/")) throw new Error("api.put only writes /api/lists/*");
    await json<void>("PUT", path, body);
  },

  tokens: {
    list: async () => (await json<{ tokens: TokenInfo[] }>("GET", "/api/tokens")).tokens,
    /** The token is returned once and never again. */
    create: (label: string) => json<{ id: string; token: string }>("POST", "/api/tokens", { label }),
    revoke: (id: string) => json<void>("DELETE", `/api/tokens/${enc(id)}`),
  },

  /** Runs one Claude task through the Worker and returns the parsed JSON result. */
  ai: async <T = unknown>(task: string, payload: AiPayload): Promise<T> =>
    (await json<{ result: T }>("POST", "/api/ai", { task, ...payload })).result,

  monday: {
    connect: (token: string) => json<{ account: string; boards: MondayBoard[] }>("POST", "/api/monday/connect", { token }),
    boards: async () => (await json<{ boards: MondayBoard[] }>("GET", "/api/monday/boards")).boards,
    /** One page of a board's items, as Monday's GraphQL returns it. Pass the previous page's cursor to continue. */
    items: (boardId: string, cursor?: string | null) => json<MondayPage>("POST", "/api/monday/items", { boardId, cursor: cursor ?? null }),
    disconnect: () => json<void>("DELETE", "/api/monday"),
  },

  activity: async () => (await json<{ activity: ActivityRow[] }>("GET", "/api/activity")).activity,
  deleteWorkspace: () => json<void>("DELETE", "/api/workspace", { confirm: "DELETE" }),
};

export type Api = typeof api;

/** Monday GraphQL page shapes (first page comes under boards[0], later pages under next_items_page). */
export interface MondayItem {
  id: string;
  name: string;
  group: { title: string } | null;
  column_values: { id: string; text: string | null; column?: { title: string } }[];
}
export interface MondayPage {
  data?: {
    boards?: { name: string; columns: { id: string; title: string; type: string }[]; items_page: { cursor: string | null; items: MondayItem[] } }[];
    next_items_page?: { cursor: string | null; items: MondayItem[] };
  };
  errors?: { message: string }[];
}
